// src/shared/SidebarNav.js

import React, {
    useEffect,
    useMemo,
    useState,
    useCallback,
} from "react";

import {
    Link,
    useLocation,
} from "react-router-dom";

import axios from "axios";


/* =========================================================
   COUNT APIs
========================================================= */

const COUNT_REPORTS =
    "https://oppiesfunc.azurewebsites.net/api/getReportCount?code=byFYwtIV0rxj8Ssajw0NMuTg3qhHbOAKASOhEI5h-UrvAzFuDQOqNQ==";

const COUNT_CLAIMS =
    "https://oppiesfunc.azurewebsites.net/api/getPendingClaimsCount?code=g3-WvOnMQnzPcnjkAugARoQfOR0AL0HKyvcKn6yvcCrcAzFuI0QzSQ==";

const COUNT_CHANGES =
    "https://oppiesfunc.azurewebsites.net/api/getPendingChangeRequestsCount?code=4qMwTjiJlBBZ4J5ZFdwVjq8BzdHc4oLgaMphQnCQ4RQKAzFuiqBvaQ==";

const COUNT_UNREAD_MESSAGES =
    "https://oppiesfunc.azurewebsites.net/api/getUnreadMessageCount?code=3JuuxgEALUx6F_k0fOITz3kF-3URbFmAWADoTLXs4f2tAzFuf6W7hQ==";

const COUNT_SHOP_REQUESTS =
    "https://oppiesfunc.azurewebsites.net/api/getPendingShopRequestsCount?code=FFeIjjit_Xiq_4uxw-WxLrEav6wRKokVvPfJ0Ac_YlinAzFuARJQuQ==";

const COUNT_STORE_REQUESTS =
    "https://oppiesfunc.azurewebsites.net/api/getPendingStoreRequestsCount?code=QLBm-VLPXgEKfxEBTH1yYFYlDolOxPHoaeX2ITfR2t9zAzFuqCN1pg==";


/*
 * Optional:
 *
 * Once getUncategorisedShops has its deployed Function URL,
 * paste it here to show the live sidebar badge.
 */

const COUNT_UNCATEGORISED_SHOPS =
    "";


/* =========================================================
   STORAGE
========================================================= */

const STORAGE_KEY =
    "oppies.sidebar.collapse.v1";


/* =========================================================
   COLOURS
========================================================= */

const OPPIES_BEIGE =
    "#bfa97c";

const TEXT_DARK =
    "#3c3329";

const TEXT_MUTED =
    "#857969";


/* =========================================================
   STYLES
========================================================= */

const styles = {

    nav: {
        position:
            "sticky",

        top:
            20,

        alignSelf:
            "flex-start",

        /*
         * Important now that the Admin Portal has enough
         * sections for the sidebar to exceed the viewport.
         *
         * Ads is currently the final section, so the sidebar
         * must be independently scrollable.
         */

        height:
            "calc(100vh - 40px)",

        maxHeight:
            "calc(100vh - 40px)",

        overflowY:
            "auto",

        overflowX:
            "hidden",

        paddingBottom:
            40,

        boxSizing:
            "border-box",

        scrollbarWidth:
            "thin",

        scrollbarColor:
            "#cfc3ad transparent",
    },


    groupWrap: {
        marginBottom:
            12,
    },


    groupHeader: (
        open
    ) => ({
        fontSize:
            12,

        color:
            TEXT_MUTED,

        padding:
            "12px 16px 8px",

        textTransform:
            "uppercase",

        letterSpacing:
            0.7,

        display:
            "flex",

        alignItems:
            "center",

        gap:
            8,

        cursor:
            "pointer",

        userSelect:
            "none",

        position:
            "relative",

        fontWeight:
            700,
    }),


    dividerLine: {
        height:
            1,

        background:
            "linear-gradient(90deg, rgba(191,169,124,0.0), rgba(191,169,124,0.35), rgba(191,169,124,0.0))",

        margin:
            "0 12px 8px",
    },


    chevron: (
        open
    ) => ({
        display:
            "inline-block",

        transition:
            "transform .15s ease",

        transform:
            `rotate(${ open? 90: 0 }deg)`,

        width:
            12,

        textAlign:
            "center",

        color:
            "#a08f7d",
    }),


    groupInner: {
        display:
            "flex",

        flexDirection:
            "column",

        gap:
            2,
    },


    item: (
        active
    ) => ({
        display:
            "flex",

        justifyContent:
            "space-between",

        alignItems:
            "center",

        padding:
            "10px 14px",

        margin:
            "0 10px",

        borderRadius:
            10,

        color:
            active
                ? "#1f5130"
                : TEXT_DARK,

        background:
            active
                ? "linear-gradient(180deg, #fffaf1 0%, #f2eee7 100%)"
                : "transparent",

        textDecoration:
            "none",

        borderLeft:
            active
                ? `3px solid ${ OPPIES_BEIGE } `
                : "3px solid transparent",

        fontWeight:
            active
                ? 800
                : 600,

        boxShadow:
            active
                ? "0 6px 16px rgba(0,0,0,0.06)"
                : "none",

        transition:
            "all .15s ease",
    }),


    itemHover: {
        background:
            "#faf6ef",

        transform:
            "translateX(1px)",
    },


    badge: (
        variant
    ) => ({
        background:
            variant ===
            "danger"
                ? "#a94442"
                : OPPIES_BEIGE,

        color:
            "#fff",

        borderRadius:
            999,

        padding:
            "2px 8px",

        fontSize:
            12,

        marginLeft:
            10,

        fontWeight:
            800,

        minWidth:
            22,

        textAlign:
            "center",

        boxShadow:
            "0 2px 8px rgba(0,0,0,0.10)",
    }),


    updating: {
        marginLeft:
            "auto",

        fontSize:
            11,

        color:
            "#a08f7d",

        fontWeight:
            600,
    },
};


/* =========================================================
   NAV ITEM
========================================================= */

function NavItem({
    to,
    label,
    badge,
    isActive,
}) {

    const [
        hover,
        setHover,
    ] =
        useState(
            false
        );


    const active =
        isActive(
            to
        );


    const badgeVariant =
        badge >= 10
            ? "danger"
            : "default";


    return (
        <Link
            to={
                to
            }
            style={{
                ...styles.item(
                    active
                ),

                ...(
                    hover &&
                    !active
                        ? styles.itemHover
                        : {}
                ),
            }}
            onMouseEnter={() =>
                setHover(
                    true
                )
            }
            onMouseLeave={() =>
                setHover(
                    false
                )
            }
        >

            <span>
                {label}
            </span>


            {badge > 0 && (

                <span
                    style={
                        styles.badge(
                            badgeVariant
                        )
                    }
                >
                    {badge}
                </span>

            )}

        </Link>
    );
}


/* =========================================================
   SIDEBAR
========================================================= */

export default function SidebarNav() {

    const {
        pathname,
    } =
        useLocation();


    /* =====================================================
       COLLAPSE STATE
    ===================================================== */

    const [
        collapsed,
        setCollapsed,
    ] =
        useState(
            () => {

                try {

                    const raw =
                        localStorage.getItem(
                            STORAGE_KEY
                        );


                    return raw
                        ? JSON.parse(
                            raw
                        )
                        : {};

                } catch {

                    return {};
                }
            }
        );


    const setGroupOpen =
        useCallback(
            (
                key,
                open
            ) => {

                setCollapsed(
                    (
                        previous
                    ) => {

                        /*
                         * NOTE:
                         *
                         * The object actually stores whether a
                         * section is COLLAPSED.
                         *
                         * open = true therefore means:
                         *
                         * collapsed[key] = false
                         */

                        const next = {
                            ...previous,

                            [key]:
                                !open,
                        };


                        try {

                            localStorage.setItem(
                                STORAGE_KEY,
                                JSON.stringify(
                                    next
                                )
                            );

                        } catch {

                            // Ignore localStorage failures.
                        }


                        return next;
                    }
                );
            },
            []
        );


    /* =====================================================
       ACTIVE ROUTE
    ===================================================== */

    const isActive =
        useCallback(
            (
                href
            ) =>
                pathname ===
                    href ||
                (
                    href !==
                        "/dashboard" &&
                    pathname.startsWith(
                        `${ href } /`
                    )
                ),
[
    pathname,
]
        );


/* =====================================================
   COUNTS
===================================================== */

const [
    counts,
    setCounts,
] =
    useState({
        reportsNew:
            0,

        pendingClaims:
            0,

        pendingChanges:
            0,

        unreadMessages:
            0,

        pendingShopRequests:
            0,

        pendingStoreRequests:
            0,

        uncategorisedShops:
            0,
    });


const [
    loadingCounts,
    setLoadingCounts,
] =
    useState(
        false
    );


const readCount =
    (
        response,
        possibleFields = []
    ) => {

        for (
            const field
            of possibleFields
        ) {

            const value =
                response
                    ?.data
                ?.[field];


            if (
                value !==
                undefined &&
                value !==
                null
            ) {

                const parsed =
                    Number(
                        value
                    );


                return Number.isFinite(
                    parsed
                )
                    ? parsed
                    : 0;
            }
        }


        return 0;
    };


/* =====================================================
   FETCH COUNTS
===================================================== */

const fetchCounts =
    useCallback(
        async () => {

            setLoadingCounts(
                true
            );


            try {

                const uncategorisedPromise =
                    COUNT_UNCATEGORISED_SHOPS
                        ? axios.get(
                            COUNT_UNCATEGORISED_SHOPS,
                            {
                                timeout:
                                    20000,
                            }
                        )
                        : Promise.resolve(
                            null
                        );


                const [
                    reportsResponse,
                    claimsResponse,
                    changesResponse,
                    messagesResponse,
                    shopsResponse,
                    storesResponse,
                    uncategorisedResponse,
                ] =
                    await Promise.all([
                        axios.get(
                            COUNT_REPORTS,
                            {
                                timeout:
                                    20000,
                            }
                        ),

                        axios.get(
                            COUNT_CLAIMS,
                            {
                                timeout:
                                    20000,
                            }
                        ),

                        axios.get(
                            COUNT_CHANGES,
                            {
                                timeout:
                                    20000,
                            }
                        ),

                        axios.get(
                            COUNT_UNREAD_MESSAGES,
                            {
                                timeout:
                                    20000,
                            }
                        ),

                        axios.get(
                            COUNT_SHOP_REQUESTS,
                            {
                                timeout:
                                    20000,
                            }
                        ),

                        axios.get(
                            COUNT_STORE_REQUESTS,
                            {
                                timeout:
                                    20000,
                            }
                        ),

                        uncategorisedPromise,
                    ]);


                const reportStatuses =
                    reportsResponse
                        ?.data
                        ?.byStatus ||
                    {};


                const reportsNew =
                    Number(
                        reportStatuses.NEW ??
                        reportsResponse
                            ?.data
                            ?.count ??
                        0
                    );


                setCounts({
                    reportsNew:
                        Number.isFinite(
                            reportsNew
                        )
                            ? reportsNew
                            : 0,

                    pendingClaims:
                        readCount(
                            claimsResponse,
                            [
                                "count",
                                "total",
                                "pendingClaims",
                            ]
                        ),

                    pendingChanges:
                        readCount(
                            changesResponse,
                            [
                                "count",
                                "total",
                                "pendingChanges",
                            ]
                        ),

                    unreadMessages:
                        readCount(
                            messagesResponse,
                            [
                                "unreadMessages",
                                "count",
                                "total",
                            ]
                        ),

                    pendingShopRequests:
                        readCount(
                            shopsResponse,
                            [
                                "pendingShopRequests",
                                "count",
                                "total",
                            ]
                        ),

                    pendingStoreRequests:
                        readCount(
                            storesResponse,
                            [
                                "pendingStoreRequests",
                                "count",
                                "total",
                            ]
                        ),

                    uncategorisedShops:
                        uncategorisedResponse
                            ? readCount(
                                uncategorisedResponse,
                                [
                                    "count",
                                    "total",
                                    "uncategorisedShops",
                                ]
                            )
                            : 0,
                });


            } catch (
            error
            ) {

                console.warn(
                    "[SidebarNav] Failed to refresh one or more counts:",
                    error
                        ?.response
                        ?.data ||
                    error
                        ?.message ||
                    error
                );


            } finally {

                setLoadingCounts(
                    false
                );
            }
        },
        []
    );


/* =====================================================
   COUNT REFRESH
===================================================== */

useEffect(
    () => {

        fetchCounts();


        const intervalId =
            setInterval(
                fetchCounts,
                30_000
            );


        const onChanged =
            () =>
                fetchCounts();


        window.addEventListener(
            "reports:changed",
            onChanged
        );

        window.addEventListener(
            "messages:changed",
            onChanged
        );

        window.addEventListener(
            "shop-requests:changed",
            onChanged
        );

        window.addEventListener(
            "store-requests:changed",
            onChanged
        );

        window.addEventListener(
            "uncategorised-shops:changed",
            onChanged
        );


        return () => {

            clearInterval(
                intervalId
            );


            window.removeEventListener(
                "reports:changed",
                onChanged
            );

            window.removeEventListener(
                "messages:changed",
                onChanged
            );

            window.removeEventListener(
                "shop-requests:changed",
                onChanged
            );

            window.removeEventListener(
                "store-requests:changed",
                onChanged
            );

            window.removeEventListener(
                "uncategorised-shops:changed",
                onChanged
            );
        };
    },
    [
        fetchCounts,
    ]
);


/* =====================================================
   ROUTE COUNT REFRESH
===================================================== */

useEffect(
    () => {

        const countSensitiveRoutes = [
            "/moderation",
            "/ops/messages",
            "/approvals",
            "/ops/uncategorised",
        ];


        if (
            countSensitiveRoutes.some(
                (
                    route
                ) =>
                    pathname.startsWith(
                        route
                    )
            )
        ) {

            fetchCounts();
        }
    },
    [
        pathname,
        fetchCounts,
    ]
);


/* =====================================================
   GROUPS
===================================================== */

const groups =
    useMemo(
        () => [

            /* =========================================
               DASHBOARD
            ========================================== */

            {
                key:
                    "dashboard",

                label:
                    "Dashboard",

                items: [
                    {
                        to:
                            "/dashboard",

                        label:
                            "Dashboard",
                    },
                ],
            },


            /* =========================================
               MODERATION
            ========================================== */

            {
                key:
                    "moderation",

                label:
                    "Moderation",

                items: [
                    {
                        to:
                            "/moderation/reports",

                        label:
                            "Reports",

                        badge:
                            counts.reportsNew,
                    },

                    {
                        to:
                            "/moderation/search",

                        label:
                            "Search",
                    },

                    {
                        to:
                            "/moderation/reviews",

                        label:
                            "Reviews",
                    },

                    {
                        to:
                            "/ops/sales",

                        label:
                            "Sales",
                    },
                ],
            },


            /* =========================================
               ROLES
            ========================================== */

            {
                key:
                    "roles",

                label:
                    "Roles",

                items: [
                    {
                        to:
                            "/roles/shoppers",

                        label:
                            "Op Shoppers",
                    },

                    {
                        to:
                            "/roles/opshops",

                        label:
                            "Op Shops",
                    },

                    {
                        to:
                            "/roles/stores",

                        label:
                            "Stores",
                    },
                ],
            },


            /* =========================================
               APPROVALS
            ========================================== */

            {
                key:
                    "approvals",

                label:
                    "Approvals",

                items: [
                    {
                        to:
                            "/approvals/managers",

                        label:
                            "Assign Manager",
                    },

                    {
                        to:
                            "/approvals/claims",

                        label:
                            "Assign Owners",

                        badge:
                            counts.pendingClaims,
                    },

                    {
                        to:
                            "/approvals/changes",

                        label:
                            "Change Requests",

                        badge:
                            counts.pendingChanges,
                    },

                    {
                        to:
                            "/approvals/shop-requests",

                        label:
                            "Shop Requests",

                        badge:
                            counts.pendingShopRequests,
                    },

                    {
                        to:
                            "/approvals/store-requests",

                        label:
                            "Store Requests",

                        badge:
                            counts.pendingStoreRequests,
                    },
                ],
            },


            /* =========================================
               OPS
            ========================================== */

            {
                key:
                    "ops",

                label:
                    "Ops",

                items: [
                    {
                        to:
                            "/ops/import",

                        label:
                            "Add Shops",
                    },

                    {
                        to:
                            "/ops/uncategorised",

                        label:
                            "Uncategorised Shops",

                        badge:
                            counts.uncategorisedShops,
                    },

                    {
                        to:
                            "/ops/announcements",

                        label:
                            "Announcements",
                    },

                    {
                        to:
                            "/ops/messages",

                        label:
                            "Messages",

                        badge:
                            counts.unreadMessages,
                    },
                ],
            },


            /* =========================================
               ADS
            ========================================== */

            {
                key:
                    "ads",

                label:
                    "Ads",

                items: [
                    {
                        to:
                            "/ads",

                        label:
                            "Manage Ads",
                    },
                ],
            },
        ],
        [
            counts.pendingClaims,
            counts.pendingChanges,
            counts.pendingShopRequests,
            counts.pendingStoreRequests,
            counts.reportsNew,
            counts.unreadMessages,
            counts.uncategorisedShops,
        ]
    );


/* =====================================================
   AUTO-OPEN ACTIVE GROUP

   If the browser has remembered a group as collapsed,
   navigating directly to one of its routes should open
   that group automatically.

   This is particularly useful for /ads.
===================================================== */

useEffect(
    () => {

        const activeGroup =
            groups.find(
                (
                    group
                ) =>
                    group.items.some(
                        (
                            item
                        ) =>
                            isActive(
                                item.to
                            )
                    )
            );


        if (
            !activeGroup
        ) {
            return;
        }


        if (
            collapsed[
            activeGroup.key
            ]
        ) {

            setCollapsed(
                (
                    previous
                ) => {

                    const next = {
                        ...previous,

                        [activeGroup.key]:
                            false,
                    };


                    try {

                        localStorage.setItem(
                            STORAGE_KEY,
                            JSON.stringify(
                                next
                            )
                        );

                    } catch {

                        // Ignore localStorage failures.
                    }


                    return next;
                }
            );
        }
    },
    [
        pathname,
        groups,
        collapsed,
        isActive,
    ]
);


/* =====================================================
   RENDER
===================================================== */

return (
    <nav
        style={
            styles.nav
        }
    >

        {groups.map(
            (
                group
            ) => {

                /*
                 * collapsed[key] === true
                 * means the section is closed.
                 */

                const open =
                    !collapsed[
                    group.key
                    ];


                return (
                    <div
                        key={
                            group.key
                        }
                        style={
                            styles.groupWrap
                        }
                    >

                        {/* =================================
                                GROUP HEADER
                            ================================== */}

                        <div
                            style={
                                styles.groupHeader(
                                    open
                                )
                            }
                            onClick={() =>
                                setGroupOpen(
                                    group.key,
                                    !open
                                )
                            }
                            title={
                                open
                                    ? "Collapse"
                                    : "Expand"
                            }
                        >

                            <span
                                style={
                                    styles.chevron(
                                        open
                                    )
                                }
                            >
                                ▶
                            </span>


                            <span>
                                {group.label}
                            </span>


                            {loadingCounts && (
                                <span
                                    style={
                                        styles.updating
                                    }
                                >
                                    updating…
                                </span>
                            )}

                        </div>


                        <div
                            style={
                                styles.dividerLine
                            }
                        />


                        {/* =================================
                                ITEMS
                            ================================== */}

                        {open && (

                            <div
                                style={
                                    styles.groupInner
                                }
                            >

                                {group.items.map(
                                    (
                                        item
                                    ) => (

                                        <NavItem
                                            key={
                                                item.to
                                            }
                                            to={
                                                item.to
                                            }
                                            label={
                                                item.label
                                            }
                                            badge={
                                                item.badge ||
                                                0
                                            }
                                            isActive={
                                                isActive
                                            }
                                        />

                                    )
                                )}

                            </div>

                        )}

                    </div>
                );
            }
        )}

    </nav>
);
}